"use strict";
/**
 * Copyright 2018 Novage LLC.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Engine = void 0;
const events_1 = require("events");
const p2p_media_loader_core_1 = require("@peertube/p2p-media-loader-core");
const segment_manager_1 = require("./segment-manager");
const hlsjs_loader_1 = require("./hlsjs-loader");
class Engine extends events_1.EventEmitter {
    constructor(settings = {}) {
        super();
        this.currentLoaders = [];
        this.loader = new p2p_media_loader_core_1.HybridLoader(settings.loader);
        this.segmentManager = new segment_manager_1.SegmentManager(this.loader, settings.segments);
        Object.keys(p2p_media_loader_core_1.Events)
            .map((eventKey) => p2p_media_loader_core_1.Events[eventKey])
            .forEach((event) => this.loader.on(event, (...args) => this.emit(event, ...args)));
    }
    static isSupported() {
        return p2p_media_loader_core_1.HybridLoader.isSupported();
    }
    createLoaderClass() {
        var _a;
        this.abortCurrentRequest();
        const engine = this; // eslint-disable-line @typescript-eslint/no-this-alias
        const loader = (_a = class {
                constructor() {
                    this.load = async (context, config, callbacks) => {
                        if (context.url.endsWith('.m3u8') !== true) {
                            engine.addLoaderImpl(this);
                        }
                        this.context = context;
                        this.callbacks = callbacks;
                        await this.impl.load(context, config, callbacks);
                    };
                    this.abort = () => {
                        if (this.context) {
                            this.impl.abort(this.context, this.callbacks);
                        }
                    };
                    this.destroy = () => {
                        if (this.context) {
                            this.impl.abort(this.context);
                        }
                        engine.removeLoaderImpl(this);
                    };
                    this.getResponseHeader = () => undefined;
                    this.impl = new hlsjs_loader_1.HlsJsLoader(engine.segmentManager);
                    this.stats = this.impl.stats;
                }
            },
            _a.getEngine = () => {
                return engine;
            },
            _a);
        return loader;
    }
    async destroy() {
        this.currentLoaders = [];
        await this.segmentManager.destroy();
    }
    abortCurrentRequest() {
        for (const loader of this.currentLoaders) {
            loader.abort();
        }
        this.currentLoaders = [];
    }
    getSettings() {
        return {
            segments: this.segmentManager.getSettings(),
            loader: this.loader.getSettings(),
        };
    }
    getDetails() {
        return {
            loader: this.loader.getDetails(),
        };
    }
    setPlayingSegment(url, byteRange, start, duration) {
        this.segmentManager.setPlayingSegment(url, byteRange, start, duration);
    }
    setPlayingSegmentByCurrentTime(playheadPosition) {
        this.segmentManager.setPlayingSegmentByCurrentTime(playheadPosition);
    }
    addLoaderImpl(loader) {
        this.currentLoaders.push(loader);
    }
    removeLoaderImpl(loader) {
        this.currentLoaders = this.currentLoaders.filter(l => l !== loader);
    }
}
exports.Engine = Engine;
